import {useEffect, useMemo, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {PatientEncounter} from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter';
import {clearSelectedEstimate} from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice';
import {loadSelectedEstimate} from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import {AppDispatch} from '../../../../shared/state/store';
import {
    estimateMatchesSelectedIoc,
    filterEstimatesByIoc,
    getEstimateListId,
    pickEstimateForIoc,
} from '../../../utils/estimate-ioc-utils';

interface UseEstimatesByIocParams {
    savedEstimations: any[] | null | undefined;
    selectedEncounter: PatientEncounter | null | undefined;
    estimate: {estimateId?: number | string | null} | null | undefined;
    estimateId?: string;
    savedEstimatesLoading: boolean;
    isLoadingEstimate: boolean;
    setIsLoadingEstimate: (loading: boolean) => void;
    selectedPatientId?: number;
    paramId?: number;
}

/**
 * Filters saved estimates by the selected IOC and keeps the selected estimate in sync
 * when the header IOC selector changes.
 */
export function useEstimatesByIoc({
    savedEstimations,
    selectedEncounter,
    estimate,
    estimateId,
    savedEstimatesLoading,
    isLoadingEstimate,
    setIsLoadingEstimate,
    selectedPatientId,
    paramId = -2,
}: UseEstimatesByIocParams) {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const hasHandledEmptySelection = useRef(false);
    const prevIocIdRef = useRef<number | undefined>(undefined);

    const selectedIocId = selectedEncounter?.patientEncounterId;
    const onEstimateRoute =
        location.pathname.includes('/estimate') &&
        !location.pathname.includes('/risk');

    const filteredEstimations = useMemo(
        () => filterEstimatesByIoc(savedEstimations, selectedIocId),
        [savedEstimations, selectedIocId]
    );

    // Call when clearing selection on purpose (e.g. New Estimate) so we don't auto-pick again
    const markEstimateSelectionHandled = () => {
        hasHandledEmptySelection.current = true;
    };

    /**
     * Keep URL/Redux estimate aligned with the header IOC selection.
     * - If the current estimate is already in filteredEstimations, no-op.
     * - Otherwise prefer the IOC-tethered estimate (pfrEstimateId), else the latest
     *   filtered estimate; if none exist, clear selection (new-estimate route).
     * - Empty selection auto-picks unless markEstimateSelectionHandled() was used
     *   (e.g. New Estimate). IOC changes reset that guard when nothing is selected.
     */
    useEffect(() => {
        if (!selectedPatientId || savedEstimatesLoading || isLoadingEstimate) {
            return;
        }

        // EstimateTab can stay mounted briefly after soft-nav to VOB; do not rewrite URL.
        if (!onEstimateRoute) {
            return;
        }

        const currentEstimateId = Number(
            estimateId || estimate?.estimateId || 0
        );
        const isInitialIocAssignment = prevIocIdRef.current === undefined;
        const iocChanged = prevIocIdRef.current !== selectedIocId;
        if (iocChanged) {
            // Allow auto-pick for the new IOC when landing with no selection
            if (!currentEstimateId) {
                hasHandledEmptySelection.current = false;
            }
            prevIocIdRef.current = selectedIocId;
        }

        // Deep link (/estimate/:id): wait for Redux to match URL before auto-pick runs.
        if (estimateId && Number(estimateId) > 0) {
            const urlEstimateId = Number(estimateId);
            if (
                !estimate?.estimateId ||
                getEstimateListId(estimate) !== urlEstimateId
            ) {
                return;
            }
        }

        const isCurrentInFiltered =
            currentEstimateId > 0 &&
            filteredEstimations.some(
                (est: any) => getEstimateListId(est) === currentEstimateId
            );

        if (isCurrentInFiltered) {
            return;
        }

        // Manual pick / full load: keep URL + Redux when they match this IOC even if
        // the saved list row id check above failed (e.g. stale list).
        if (
            currentEstimateId > 0 &&
            estimate?.estimateId &&
            getEstimateListId(estimate) === currentEstimateId &&
            estimateMatchesSelectedIoc(estimate, selectedIocId)
        ) {
            return;
        }

        // Header IOC may lag behind a deep-linked estimate; hydrate from estimate instead of re-picking.
        if (
            isInitialIocAssignment &&
            estimateId &&
            Number(estimateId) === currentEstimateId &&
            estimate?.estimateId &&
            getEstimateListId(estimate) === currentEstimateId &&
            !estimateMatchesSelectedIoc(estimate, selectedIocId)
        ) {
            return;
        }

        // Empty selection: auto-pick unless New Estimate (or similar) already handled it
        if (!currentEstimateId) {
            if (hasHandledEmptySelection.current || estimate) {
                return;
            }
        } else if (!selectedIocId) {
            // Have an estimate but no IOC to sync against
            return;
        }

        // Prefer tethered estimate, else latest filtered
        const target = pickEstimateForIoc(
            filteredEstimations,
            selectedIocId,
            selectedEncounter?.pfrEstimateId
        );
        const targetId = getEstimateListId(target);

        if (!targetId) {
            // Nothing to pick yet (e.g. unlinked rows still being assigned an IOC).
            // Do not mark handled, retry when the filtered list updates
            if (currentEstimateId) {
                dispatch(clearSelectedEstimate());
                navigate(`/patients/${selectedPatientId}/estimate`, {
                    replace: true,
                });
            }
            return;
        }

        hasHandledEmptySelection.current = true;
        if (targetId === currentEstimateId) {
            return;
        }

        setIsLoadingEstimate(true);
        navigate(`/patients/${selectedPatientId}/estimate/${targetId}`, {
            replace: true,
        });
        dispatch(
            loadSelectedEstimate({
                paramId,
                estimationId: targetId,
            })
        ).finally(() => {
            setIsLoadingEstimate(false);
        });
    }, [
        selectedIocId,
        filteredEstimations,
        estimateId,
        estimate,
        selectedEncounter?.pfrEstimateId,
        selectedPatientId,
        savedEstimatesLoading,
        isLoadingEstimate,
        setIsLoadingEstimate,
        dispatch,
        navigate,
        paramId,
        onEstimateRoute,
    ]);

    return {
        filteredEstimations,
        selectedIocId,
        markEstimateSelectionHandled,
    };
}
